import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Dialog,
  DialogContent,
  IconButton,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Chip,
  Paper,
} from '@mui/material';
import {
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  Image as ImageIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { multimodalAPI } from '../services/api';

interface DocumentImage {
  id: string;
  page_number: number;
  page_label: string;
  image_index: number;
  image_data: string;
  format: string;
  width: number;
  height: number;
  position?: {
    x0: number;
    y0: number;
    x1: number;
    y1: number;
  };
}

interface DocumentThumbnail {
  id: string;
  page_number: number;
  page_label: string;
  thumbnail_data: string;
  width: number;
  height: number;
  dpi: number;
}

interface DocumentMultimodalViewerProps {
  documentId: string;
  documentName?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`multimodal-tabpanel-${index}`}
      aria-labelledby={`multimodal-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const DocumentMultimodalViewer: React.FC<DocumentMultimodalViewerProps> = ({
  documentId,
  documentName,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [thumbnails, setThumbnails] = useState<DocumentThumbnail[]>([]);
  const [images, setImages] = useState<DocumentImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const [zoom, setZoom] = useState(1);

  const loadMultimodalContent = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [thumbnailsData, imagesData] = await Promise.all([
        multimodalAPI.getDocumentThumbnails(documentId),
        multimodalAPI.getDocumentImages(documentId),
      ]);

      setThumbnails(thumbnailsData.thumbnails || []);
      setImages(imagesData.images || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load multimodal content');
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    loadMultimodalContent();
  }, [documentId, loadMultimodalContent]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleImageClick = (imageData: string, index: number) => {
    setSelectedImage(imageData);
    setSelectedImageIndex(index);
    setZoom(1);
  };

  const handleCloseDialog = () => {
    setSelectedImage(null);
    setZoom(1);
  };

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.25, 0.5));
  };

  const handlePrevImage = () => {
    const allImages = tabValue === 0 ? thumbnails : images;
    const newIndex = (selectedImageIndex - 1 + allImages.length) % allImages.length;
    setSelectedImageIndex(newIndex);
    setSelectedImage(
      tabValue === 0
        ? (allImages[newIndex] as DocumentThumbnail).thumbnail_data
        : (allImages[newIndex] as DocumentImage).image_data
    );
  };

  const handleNextImage = () => {
    const allImages = tabValue === 0 ? thumbnails : images;
    const newIndex = (selectedImageIndex + 1) % allImages.length;
    setSelectedImageIndex(newIndex);
    setSelectedImage(
      tabValue === 0
        ? (allImages[newIndex] as DocumentThumbnail).thumbnail_data
        : (allImages[newIndex] as DocumentImage).image_data
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (thumbnails.length === 0 && images.length === 0) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No multimodal content available for this document. This feature is only available for PDF documents.
      </Alert>
    );
  }

  return (
    <Box>
      <Paper elevation={0} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="multimodal content tabs">
          <Tab
            icon={<DescriptionIcon />}
            label={`Page Thumbnails (${thumbnails.length})`}
            iconPosition="start"
          />
          <Tab
            icon={<ImageIcon />}
            label={`Extracted Images (${images.length})`}
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      <TabPanel value={tabValue} index={0}>
        {thumbnails.length > 0 ? (
          <Grid container spacing={2}>
            {thumbnails.map((thumbnail, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={thumbnail.id}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      boxShadow: 4,
                    },
                  }}
                  onClick={() => handleImageClick(thumbnail.thumbnail_data, index)}
                >
                  <CardMedia
                    component="img"
                    image={thumbnail.thumbnail_data}
                    alt={`Page ${thumbnail.page_number}`}
                    sx={{
                      height: 200,
                      objectFit: 'contain',
                      bgcolor: 'grey.100',
                    }}
                  />
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      Page {thumbnail.page_number}
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      <Chip label={`${thumbnail.width}x${thumbnail.height}`} size="small" />
                      <Chip label={`${thumbnail.dpi} DPI`} size="small" />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Alert severity="info">No page thumbnails available</Alert>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {images.length > 0 ? (
          <Grid container spacing={2}>
            {images.map((image, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={image.id}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      boxShadow: 4,
                    },
                  }}
                  onClick={() => handleImageClick(image.image_data, index)}
                >
                  <CardMedia
                    component="img"
                    image={image.image_data}
                    alt={`Image ${image.image_index} from page ${image.page_number}`}
                    sx={{
                      height: 200,
                      objectFit: 'contain',
                      bgcolor: 'grey.100',
                    }}
                  />
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      Page {image.page_number} - Image {image.image_index + 1}
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      <Chip label={`${image.width}x${image.height}`} size="small" />
                      <Chip label={image.format.toUpperCase()} size="small" color="primary" />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Alert severity="info">No extracted images available</Alert>
        )}
      </TabPanel>

      {/* Image Viewer Dialog */}
      <Dialog
        open={selectedImage !== null}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'grey.900',
            minHeight: '80vh',
          },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            display: 'flex',
            gap: 1,
          }}
        >
          <IconButton onClick={handleZoomOut} sx={{ color: 'white', bgcolor: 'rgba(0,0,0,0.5)' }}>
            <ZoomOutIcon />
          </IconButton>
          <IconButton onClick={handleZoomIn} sx={{ color: 'white', bgcolor: 'rgba(0,0,0,0.5)' }}>
            <ZoomInIcon />
          </IconButton>
          <IconButton onClick={handleCloseDialog} sx={{ color: 'white', bgcolor: 'rgba(0,0,0,0.5)' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 0,
            position: 'relative',
            overflow: 'auto',
          }}
        >
          <IconButton
            onClick={handlePrevImage}
            sx={{
              position: 'absolute',
              left: 16,
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.5)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
            }}
          >
            <NavigateBeforeIcon />
          </IconButton>

          {selectedImage && (
            <img
              src={selectedImage}
              alt="Full size view"
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                transform: `scale(${zoom})`,
                transition: 'transform 0.2s',
              }}
            />
          )}

          <IconButton
            onClick={handleNextImage}
            sx={{
              position: 'absolute',
              right: 16,
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.5)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
            }}
          >
            <NavigateNextIcon />
          </IconButton>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DocumentMultimodalViewer;
